import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeftIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminFeedback = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await axios.get(`${API_URL}/feedback`);
        setFeedback(res.data);
      } catch (err) {
        console.error('Error fetching feedback:', err);
        setError('Failed to load feedback.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchFeedback();
    }
  }, [user]);

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/feedback/${id}/read`, {});
      setFeedback(prev => prev.map(f => f._id === id ? { ...f, status: 'read' } : f));
    } catch (err) {
      console.error('Error marking feedback as read:', err);
    }
  };

  const categoryLabel = {
    general: 'General',
    bug: 'Bug Report',
    feature: 'Feature Request',
    improvement: 'Improvement'
  };

  const categoryColor = {
    general: 'bg-gray-100 text-gray-700',
    bug: 'bg-red-100 text-red-700',
    feature: 'bg-blue-100 text-blue-700',
    improvement: 'bg-green-100 text-green-700'
  };

  if (user && user.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">User Feedback</h1>
          <p className="text-gray-600 mt-1">{feedback.length} submission{feedback.length !== 1 ? 's' : ''}</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {feedback.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No feedback yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => (
              <div
                key={item._id}
                className={`bg-white rounded-lg shadow p-6 border-l-4 ${
                  item.status === 'new' ? 'border-l-primary-500' : 'border-l-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${categoryColor[item.category] || categoryColor.general}`}>
                        {categoryLabel[item.category] || 'General'}
                      </span>
                      {item.status === 'new' && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                          New
                        </span>
                      )}
                    </div>
                    {item.email && (
                      <p className="text-sm text-gray-500 mb-2">{item.email}</p>
                    )}
                    <p className="text-gray-700 whitespace-pre-wrap">{item.message}</p>
                    <p className="text-xs text-gray-400 mt-3">
                      {new Date(item.createdAt).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </p>
                  </div>
                  {item.status === 'new' && (
                    <button
                      onClick={() => markAsRead(item._id)}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-green-600 transition"
                      title="Mark as read"
                    >
                      <CheckCircleIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
